import { useEffect, useState } from "react";
import DrillDownModal from "@/components/DrillDownModal";
import api from "@/lib/api";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { formatMoney } from "@/components/receivables/shared";

export function AgingTab() {
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  const { isActive, filterRails } = useCurrencyRail();
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports/aged-receivables");
        setData(data);
      } catch (err) {
        console.warn("[Receivables] aged-receivables failed:", err?.message);
      }
    })();
  }, []);
  if (!data) return <div className="text-sm text-[var(--muted-text)]">Loading…</div>;
  const buckets = ["current", "1-30", "31-60", "61-90", "90+"];
  const rails = filterRails(data.by_currency || []);
  return (
    <div className="space-y-4">
      {/* Per-currency aging cards — no cross-currency summing */}
      {rails.map((rail) => (
        <div key={`rail-${rail.currency}`} data-testid={`ar-rail-${rail.currency}`} className="space-y-2">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">
            <span className="px-1.5 py-0.5 rounded font-mono text-[10px] text-[#FDFCF8]"
                  style={{ backgroundColor: rail.currency === "USD" ? "#1E5A96" : "#4C956C" }}>{rail.currency}</span>
            <span>rail</span>
          </div>
          <div className="grid grid-cols-6 gap-3">
            {buckets.map((b) => (
              <div key={b} className="card-flat p-4 cursor-pointer hover:border-[var(--brand)] transition-colors"
                   onClick={() => setDrill({ title: `Aged Receivables · ${rail.currency} · ${b}`, params: { kind: "receivable", bucket: b } })}>
                <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">{b}</div>
                <div className="font-mono-data text-lg mt-1" data-testid={`ar-bucket-${rail.currency}-${b}`}>{formatMoney(rail[b], rail.currency)}</div>
              </div>
            ))}
            <div className="card-flat p-4 border-l-4 border-l-[#D4A017] cursor-pointer hover:border-l-[var(--brand)]"
                 onClick={() => setDrill({ title: `Aged Receivables · ${rail.currency} · Total`, params: { kind: "receivable" } })}>
              <div className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Total</div>
              <div className="font-mono-data text-lg mt-1" data-testid={`ar-bucket-${rail.currency}-total`}>{formatMoney(rail.total, rail.currency)}</div>
            </div>
          </div>
        </div>
      ))}
      <div className="card-flat overflow-hidden">
        {(() => { const visible = data.customers.filter((c) => isActive(c.currency || "USD")); return visible.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--muted-text)]">
            {data.customers.length === 0 ? "Nothing outstanding — all paid up!" : "No customers on the selected currency rail."}
          </div>
        ) : (
          <table className="qb-table w-full text-sm">
            <thead className="bg-[var(--hover)] text-xs uppercase tracking-wider text-[var(--muted-text)]">
              <tr><th className="text-left px-5 py-2.5">Customer</th><th className="text-left px-3 py-2.5">Ccy</th>{buckets.map((b) => <th key={b} className="text-right px-3 py-2.5">{b}</th>)}<th className="text-right px-4 py-2.5">Total</th></tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr
                  key={c.customer_id}
                  data-testid={`ar-row-${c.customer_id}`}
                  onClick={() => setDrill({
                    title: `${c.customer_name} · ${formatMoney(c.total, c.currency)}`,
                    params: { kind: "receivable", customer_id: c.customer_id },
                  })}
                  className="cursor-pointer border-b border-[var(--divider)] last:border-none"
                >
                  <td className="px-5 py-3 font-medium">{c.customer_name}</td>
                  <td className="px-3 py-3 font-mono text-[10px]"><span className="px-1.5 py-0.5 rounded bg-[var(--hover)]">{c.currency || "USD"}</span></td>
                  {buckets.map((b) => <td key={b} className="px-3 py-3 text-right font-mono">{c.buckets[b] > 0 ? formatMoney(c.buckets[b], c.currency) : "—"}</td>)}
                  <td className="px-4 py-3 text-right font-mono font-semibold">{formatMoney(c.total, c.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ); })()}
      </div>
      <DrillDownModal
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        params={drill?.params}
      />
    </div>
  );
}
